import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

type HintPlacement =
    | "top"
    | "top-left"
    | "top-right"
    | "bottom"
    | "bottom-left"
    | "bottom-right"
    | "left"
    | "left-top"
    | "left-bottom"
    | "right"
    | "right-top"
    | "right-bottom";

interface HintState {
    element: HTMLElement;
    text: string;
    placement: HintPlacement;
    offsetX: number;
    offsetY: number;
    background: string;
    color: string;
    padding: string;
    radius: string;
    fontSize: string;
    fontWeight: string;
    arrowSize: number;
    transitionDuration: string;
}

interface Position {
    top: number;
    left: number;
}

const getNumber = (
    element: HTMLElement,
    attribute: string,
    cssVariable: string,
    fallback: number
) => {
    const attributeValue = element.getAttribute(attribute);

    if (attributeValue !== null) {
        const value = Number.parseFloat(attributeValue);
        return Number.isFinite(value) ? value : fallback;
    }

    const cssValue = getComputedStyle(element)
        .getPropertyValue(cssVariable)
        .trim();

    const value = Number.parseFloat(cssValue);

    return Number.isFinite(value) ? value : fallback;
};

const getHintState = (
    element: HTMLElement
): HintState | null => {
    const text = element.getAttribute("data-hint");

    if (!text) {
        return null;
    }

    const style = getComputedStyle(element);

    return {
        element,
        text,
        placement:
            (element.getAttribute(
                "data-hint-placement"
            ) as HintPlacement) || "top",

        offsetX: getNumber(
            element,
            "data-hint-offset-x",
            "--hint-offset-x",
            0
        ),

        offsetY: getNumber(
            element,
            "data-hint-offset-y",
            "--hint-offset-y",
            0
        ),

        background:
            style.getPropertyValue("--hint-bg").trim() ||
            "#E4E4E4",

        color:
            style.getPropertyValue("--hint-color").trim() ||
            "#000",

        padding:
            style.getPropertyValue("--hint-padding").trim() ||
            "5px 10px",

        radius:
            style.getPropertyValue("--hint-radius").trim() ||
            "2px",

        fontSize:
            style
                .getPropertyValue("--hint-font-size")
                .trim() || "0.85rem",

        fontWeight:
            style
                .getPropertyValue("--hint-font-weight")
                .trim() || "normal",

        arrowSize: getNumber(
            element,
            "data-hint-arrow-size",
            "--hint-arrow-size",
            5
        ),

        transitionDuration:
            style
                .getPropertyValue("--hint-transition-duration")
                .trim() || "0.12s",
    };
};

const calculatePosition = (
    element: HTMLElement,
    placement: HintPlacement,
    offsetX: number,
    offsetY: number,
    width: number,
    height: number
): Position => {
    const rect = element.getBoundingClientRect();
    const gap = 5;

    switch (placement) {
        case "top":
            return {
                left:
                    rect.left +
                    rect.width / 2 -
                    width / 2 + 
                    offsetX,
                top:
                    rect.top -
                    height -
                    gap +
                    offsetY,
            };

        case "top-left":
            return {
                left: rect.left + offsetX,
                top:
                    rect.top -
                    height -
                    gap +
                    offsetY,
            };

        case "top-right":
            return {
                left:
                    rect.right -
                    width +
                    offsetX,
                top:
                    rect.top -
                    height -
                    gap +
                    offsetY,
            };

        case "bottom":
            return {
                left:
                    rect.left +
                    rect.width / 2 -
                    width / 2 +
                    offsetX,
                top:
                    rect.bottom +
                    gap +
                    offsetY,
            };

        case "bottom-left":
            return {
                left: rect.left + offsetX,
                top:
                    rect.bottom +
                    gap +
                    offsetY,
            };

        case "bottom-right":
            return {
                left:
                    rect.right -
                    width +
                    offsetX,
                top:
                    rect.bottom +
                    gap +
                    offsetY,
            };

        case "left":
            return {
                left:
                    rect.left -
                    width -
                    gap +
                    offsetX,
                top:
                    rect.top +
                    rect.height / 2 -
                    height / 2 +
                    offsetY,
            };

        case "left-top":
            return {
                left:
                    rect.left -
                    width -
                    gap +
                    offsetX,
                top:
                    rect.top +
                    offsetY,
            };

        case "left-bottom":
            return {
                left:
                    rect.left -
                    width -
                    gap +
                    offsetX,
                top:
                    rect.bottom -
                    height +
                    offsetY,
            };

        case "right":
            return {
                left:
                    rect.right +
                    gap +
                    offsetX,
                top:
                    rect.top +
                    rect.height / 2 -
                    height / 2 +
                    offsetY,
            };

        case "right-top":
            return {
                left:
                    rect.right +
                    gap +
                    offsetX,
                top:
                    rect.top +
                    offsetY,
            };

        case "right-bottom":
            return {
                left:
                    rect.right +
                    gap +
                    offsetX,
                top:
                    rect.bottom -
                    height +
                    offsetY,
            };
    }
};

const getArrowPosition = (
    element: HTMLElement,
    placement: HintPlacement,
    offsetX: number,
    offsetY: number,
    size: number
): Position => {
    const rect = element.getBoundingClientRect();
    const gap = 5;

    switch (placement) {
        case "top":
        case "top-left":
        case "top-right":
            return {
                left:
                    rect.left +
                    rect.width / 2 -
                    size +
                    offsetX,
                top:
                    rect.top -
                    gap +
                    offsetY,
            };

        case "bottom":
        case "bottom-left":
        case "bottom-right":
            return {
                left:
                    rect.left +
                    rect.width / 2 -
                    size +
                    offsetX,
                top:
                    rect.bottom +
                    gap -
                    size +
                    offsetY,
            };

        case "left":
        case "left-top":
        case "left-bottom":
            return {
                left:
                    rect.left -
                    gap -
                    size / 2 +
                    offsetX,
                top:
                    rect.top +
                    rect.height / 2 -
                    size / 2 +
                    offsetY,
            };

        case "right":
        case "right-top":
        case "right-bottom":
            return {
                left:
                    rect.right +
                    gap -
                    size * 1.5 +
                    offsetX,
                top:
                    rect.top +
                    rect.height / 2 -
                    size / 2 +
                    offsetY,
            };
    }
};

export const GlobalHint = () => {
    const [hint, setHint] =
        useState<HintState | null>(null);

    const [position, setPosition] =
        useState<Position>({
            top: 0,
            left: 0,
        });

    const [arrowPosition, setArrowPosition] =
        useState<Position>({
            top: 0,
            left: 0,
        });

    const hintRef =
        useRef<HTMLDivElement | null>(null);

    const updatePosition = useCallback(() => {
        if (!hint || !hintRef.current) {
            return;
        }

        const rect =
            hintRef.current.getBoundingClientRect();

        setPosition(
            calculatePosition(
                hint.element,
                hint.placement,
                hint.offsetX,
                hint.offsetY,
                rect.width,
                rect.height
            )
        );

        setArrowPosition(
            getArrowPosition(
                hint.element,
                hint.placement,
                hint.offsetX,
                hint.offsetY,
                hint.arrowSize
            )
        );
    }, [hint]);

    useEffect(() => {
        if (!hint) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (!hint.element.isConnected) {
                setHint(null);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, [hint]);

    useEffect(() => {
        const handlePointerOver = (
            event: PointerEvent
        ) => {
            const target =
                event.target as HTMLElement | null;

            if (!target) {
                return;
            }

            const element =
                target.closest<HTMLElement>(".hint");

            if (!element) {
                return;
            }

            const nextHint =
                getHintState(element);

            if (nextHint) {
                setHint(nextHint);
            }
        };

        const handlePointerOut = (
            event: PointerEvent
        ) => {
            const target =
                event.target as HTMLElement | null;

            if (!target) {
                return;
            }

            const element =
                target.closest<HTMLElement>(".hint");

            if (!element) {
                return;
            }

            const relatedTarget =
                event.relatedTarget as Node | null;

            if (
                relatedTarget &&
                element.contains(relatedTarget)
            ) {
                return;
            }

            setHint((current) =>
                current?.element === element
                    ? null
                    : current
            );
        };

        document.addEventListener(
            "pointerover",
            handlePointerOver
        );

        document.addEventListener(
            "pointerout",
            handlePointerOut
        );

        return () => {
            document.removeEventListener(
                "pointerover",
                handlePointerOver
            );

            document.removeEventListener(
                "pointerout",
                handlePointerOut
            );
        };
    }, []);

    useLayoutEffect(() => {
        updatePosition();
    }, [updatePosition]);

    useEffect(() => {
        if (!hint) {
            return;
        }

        const update = () => {
            updatePosition();
        };

        window.addEventListener(
            "resize",
            update
        );

        window.addEventListener(
            "scroll",
            update,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                update
            );

            window.removeEventListener(
                "scroll",
                update,
                true
            );
        };
    }, [hint, updatePosition]);

    if (!hint) {
        return null;
    }

    const hintStyle: CSSProperties = {
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        zIndex: 2147483647,
        background: hint.background,
        color: hint.color,
        padding: hint.padding,
        borderRadius: hint.radius,
        fontSize: hint.fontSize,
        fontWeight: hint.fontWeight,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        pointerEvents: "none",
    };

    let arrowTransform = "";

    if (hint.placement.startsWith("bottom")) {
        arrowTransform = "rotate(180deg)";
    } else if (hint.placement.startsWith("left")) {
        arrowTransform = "rotate(-90deg)";
    } else if (hint.placement.startsWith("right")) {
        arrowTransform = "rotate(90deg)";
    }
    const arrowStyle: CSSProperties = {
        position: "fixed",
        left: `${arrowPosition.left}px`,
        top: `${arrowPosition.top}px`,
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: `${hint.arrowSize}px ${hint.arrowSize}px 0 ${hint.arrowSize}px`,
        borderColor: `${hint.background} transparent transparent transparent`,
        transform: arrowTransform,
        zIndex: 2147483647,
        pointerEvents: "none",
    };

    return createPortal(
        <>
            <div
                ref={hintRef}
                style={hintStyle}
            >
                {hint.text}
            </div>

            <div style={arrowStyle} />
        </>,
        document.body
    );
};